/**
 * Single source of truth for the free-tier sample-scan budget.
 *
 * Issue #515 replaced the flat 1,000-email free sample with a sender-centric
 * sample: we still *fetch* up to `fetchTotal` emails (top `senderCap` senders ×
 * `fetchPerSender`) so cold-start sender stats stay rich, but we only run the
 * LLM classifier on the latest `classifyPerSender` emails per selected sender
 * (`classifyTotal` total). That is the ~10x LLM-cost reduction.
 *
 * Every code path - subscription quota display, scan-job `smart_sample_target`,
 * scan-worker hard ceiling, the SmartSampleGoals schema default, cold-start
 * per-sender sampling, and the onboarding progress denominator - must read from
 * this object. Public pricing copy must format these constants instead of
 * hardcoding a separate number.
 *
 * Do NOT read `process.env.FREE_TIER_EMAIL_LIMIT` anywhere; that legacy env var
 * produced inconsistent defaults across the backend and is deliberately removed.
 */
export const FREE_SAMPLE = {
  /** Number of top senders (by volume) the free sample analyses. */
  senderCap: 10,
  /** Max emails fetched per selected sender (feeds cold-start stats). */
  fetchPerSender: 100,
  /** Max emails classified by the LLM per selected sender. */
  classifyPerSender: 10,
  /** Total emails fetched/persisted across all selected senders. */
  fetchTotal: 1000,
  /** Total emails classified by the LLM across all selected senders. */
  classifyTotal: 100,
} as const;

/**
 * @deprecated Use `FREE_SAMPLE.fetchTotal` (fetch ceiling) or
 * `FREE_SAMPLE.classifyTotal` (classify/denominator ceiling) directly. Retained
 * so existing marketing/doc consumers keep compiling; it equals the fetch total.
 */
export const FREE_TIER_EMAIL_CAP = FREE_SAMPLE.fetchTotal;
